import useQuery from 'hooks/useQuery'
import getAccessToken from 'hooks/getAccessToken'

const { REACT_APP_API_URL } = process.env

// Mismo criterio que useHceSummaryApi.js#postJson: fetch directo, NUNCA
// hooks/useMutation (esa sincroniza el cache por substring del endpoint —
// "consumers"/"events" — y esta URL puede contener ambos según la pantalla;
// ya causó un crash real en prod documentado en useMutation.js:112-124).
const request = async (method, path, accessToken, body) => {
  const res = await fetch(`${REACT_APP_API_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  })
  const data = await res.json().catch(() => null)
  if (!res.ok) {
    const error = new Error(data?.error || `http_${res.status}`)
    error.status = res.status
    error.response = data
    throw error
  }
  return data
}

// Bug real (reporte QA, 2026-08-31): `POST /escriba/grabaciones` devuelve
// `id` PREFIJADO (`escriba_recordings/<hex>`, mismo criterio que cualquier
// otro id de DrApp — consumers/events/records), pero TODAS las rutas de
// escriba con `:recordingID` en la URL esperan el hex PELADO (el propio
// server vuelve a anteponer el prefijo internamente, ver
// requireRecordingOwner en api/src/escriba/permissions.js). El `id` sin
// pelar se sustituía tal cual en el path de `claim()` →
// `/escriba/grabaciones/escriba_recordings/<hex>/claim` → 404. Acepta las
// dos formas (con o sin prefijo) para no importar de dónde venga el id —
// mismo criterio que `normalizeId`/`bareId` de Igor para este mismo
// problema en otros repos.
const bareRecordingId = id => (id || '').split('/').pop()

/**
 * Cliente de los 18 endpoints de Escriba. Capa chica y sin estado propio a
 * propósito (mismo criterio que useOdontogramaApi.js): la orquestación de
 * MediaRecorder/heartbeat/polling vive en los componentes que lo usan, no
 * acá — este hook sólo sabe hablarle al server.
 *
 * `path` siempre viene armado con `window._l(...)`: resuelve el teamID
 * desde la URL actual (`/teams/<id>/...`), igual que cada hook de
 * beta-features existente.
 */
export function useEscribaApi({ enabled: enabledFromCaller } = {}) {
  const accessToken = getAccessToken()
  const token = () => accessToken.value
  // Bug real (reporte QA, 2026-08-31): el poll inicial de EscribaMobile.js
  // corría antes de que este mismo hook resolviera el token (Auth0 pide el
  // token async, sin esperar dentro del efecto) — salía sin Authorization,
  // el server respondía 401, y la UI lo mostraba como "no encontrada". Se
  // expone acá para que el caller pueda esperar a que haya token antes de
  // disparar el primer request autenticado.
  const accessTokenReady = !!accessToken.value
  const accessTokenError = accessToken.error || null

  const flagFromCaller = typeof enabledFromCaller === 'boolean'
  const enabledQuery = useQuery(window._l('/escriba/enabled').substr(1), {
    staleTime: 10000,
    enabled: !flagFromCaller,
  })
  const enabled = flagFromCaller ? enabledFromCaller : enabledQuery.data?.enabled === true

  const getCurrentConsent = consumerID =>
    request('GET', window._l(`/escriba/consentimientos/vigente/${consumerID}`), token())

  const createConsent = body =>
    request('POST', window._l('/escriba/consentimientos'), token(), body)

  const revokeConsent = (consentID, reason) =>
    request('POST', window._l(`/escriba/consentimientos/${consentID}/revocar`), token(), { reason })

  const createRecording = body =>
    request('POST', window._l('/escriba/grabaciones'), token(), body)

  const claim = (recordingID, body) =>
    request('POST', window._l(`/escriba/grabaciones/${bareRecordingId(recordingID)}/claim`), token(), body)

  const requestSegments = (recordingID, body) =>
    request('POST', window._l(`/escriba/grabaciones/${bareRecordingId(recordingID)}/segmentos`), token(), body)

  const heartbeat = (recordingID, body) =>
    request('POST', window._l(`/escriba/grabaciones/${bareRecordingId(recordingID)}/heartbeat`), token(), body)

  const requestClose = recordingID =>
    request('POST', window._l(`/escriba/grabaciones/${bareRecordingId(recordingID)}/cerrar`), token(), {})

  const complete = (recordingID, body) =>
    request('POST', window._l(`/escriba/grabaciones/${bareRecordingId(recordingID)}/completar`), token(), body)

  const abort = (recordingID, reason) =>
    request('POST', window._l(`/escriba/grabaciones/${bareRecordingId(recordingID)}/abortar`), token(), { reason })

  const poll = recordingID =>
    request('GET', window._l(`/escriba/grabaciones/${bareRecordingId(recordingID)}`), token())

  const getTranscript = recordingID =>
    request('GET', window._l(`/escriba/grabaciones/${bareRecordingId(recordingID)}/transcripcion`), token())

  const getDownload = (recordingID, kind = 'audio') =>
    request('GET', window._l(`/escriba/grabaciones/${bareRecordingId(recordingID)}/descarga?kind=${kind}`), token())

  const getPending = consumerID =>
    request('GET', window._l(`/escriba/pendientes/${consumerID}`), token())

  const retry = recordingID =>
    request('POST', window._l(`/escriba/grabaciones/${bareRecordingId(recordingID)}/reintentar`), token(), {})

  const rate = (recordingID, rating, comment, runId) =>
    request('POST', window._l(`/escriba/grabaciones/${bareRecordingId(recordingID)}/rating`), token(), { rating, comment, runId })

  const onboardingAck = textVersion =>
    request('POST', window._l('/escriba/onboarding-ack'), token(), { textVersion })

  // POST directo a S3 con la policy firmada: sin Authorization (S3 no lo
  // quiere) y con los `fields` del server antes del archivo (S3 ignora todo
  // campo posterior a `file`). Key, tipo, cifrado y tamaño máximo los hace
  // cumplir la policy; un 403 es policy vencida o que no coincide.
  const uploadSegmentToS3 = async ({ url, fields }, blob) => {
    const form = new FormData()
    Object.entries(fields).forEach(([name, value]) => form.append(name, value))
    form.append('file', blob)
    const res = await fetch(url, { method: 'POST', body: form })
    if (!res.ok) {
      const error = new Error(`s3_post_${res.status}`)
      error.status = res.status
      throw error
    }
  }

  return {
    enabled, enabledQuery,
    accessTokenReady, accessTokenError,
    getCurrentConsent, createConsent, revokeConsent,
    createRecording, claim, requestSegments, heartbeat, requestClose, complete, abort,
    poll, getTranscript, getDownload, getPending, retry, rate, onboardingAck,
    uploadSegmentToS3,
  }
}

export default useEscribaApi
